import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { t } from '@lingui/macro'
import styled, { useTheme } from 'styled-components'

import { BottomSheet } from '@/@bothrs-ui/web/components/bottom-sheet/BottomSheet'
import { Flex, FlexRow } from '@/@bothrs-ui/web/components/layout'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { H2 } from '@/@bothrs-ui/web/components/typography/Heading'
import { ContentPagePaddingCSS } from '@/layout/components/content-page-layout/ContentPageLayout.styled'
import { CareQuestionOptionCta } from '../../../care-question-option-cta/CareQuestionOptionCta'

import type { FC } from 'react'
import type { BottomSheetProps } from '@/@bothrs-ui/web/components/bottom-sheet/BottomSheet.props'
import type { CareQuestionOption } from '@/modules/care-question/hooks/useCareQuestionOptions'

export type CareQuestionBottomSheetProps = Omit<BottomSheetProps, 'children' | 'steps' | 'draggable'> & {
  careQuestionOptions: CareQuestionOption[]
  onClose: () => void
}

/**
 * Care question bottom sheet
 */
export const CareQuestionBottomSheet: FC<CareQuestionBottomSheetProps> = ({
  visible,
  onClose,
  careQuestionOptions,
}) => {
  const theme = useTheme()

  // Render
  // ------------------------------------------------------------------------- /

  return (
    <BottomSheet fitContent draggable={false} visible={visible} closeCtaVisible={false} onClose={onClose}>
      <StContainer gap={theme.UI.Spacing.Space6}>
        <FlexRow justifyContent='space-between'>
          <H2 color={theme.Colors.Theme.Primary} noMargin>
            {t({
              id: 'floating-action-button.bottom-sheet.title',
              message: 'Wat wil je doen?',
            })}
          </H2>

          <StCloseButton
            title={t({
              id: 'bottomsheet.close-title',
              comment: 'Title-attribute for the button that closes the BottomSheet',
              message: 'Overlay sluiten',
            })}
            onClick={onClose}
          >
            <Span type='BodyMediumBold' color={theme.Colors.Neutrals.Darkest} style={{ fontSize: 16 }}>
              {t({ id: 'globals.close', message: 'Sluiten' })}
            </Span>
            <FontAwesomeIcon icon={['fal', 'times']} fontSize={16} color={theme.Colors.Theme.Primary} />
          </StCloseButton>
        </FlexRow>

        <Flex flexDirection='column' gap={theme.UI.Spacing.Space4}>
          {careQuestionOptions.map((option) => (
            <StOptionContainer key={option.id}>
              <CareQuestionOptionCta {...option} onClose={onClose} />
            </StOptionContainer>
          ))}
        </Flex>
      </StContainer>
    </BottomSheet>
  )
}

const StContainer = styled(Flex)`
  gap: ${({ theme }) => theme.UI.Spacing.Space6}px;
  flex-direction: column;
  ${ContentPagePaddingCSS}

  padding-top: 0;
  padding-bottom: ${({ theme }) => theme.UI.Spacing.Space4}px;
`

const StOptionContainer = styled(Flex)`
  border: 1px solid #eaeaeb;
  border-radius: ${({ theme }) => theme.UI.BorderRadius.Large}px;
`

const StCloseButton = styled(Flex)`
  cursor: pointer;
  align-items: center;

  span {
    color: ${({ theme }) => theme.Colors.Theme.Primary};
  }

  gap: ${({ theme }) => theme.UI.Spacing.Space2}px;
`
